import React, {
    Dispatch,
    KeyboardEvent,
    memo,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import styles from "./SlideCommentPanel.module.css";
import { useFormTextInput } from "../../../../common/useFormTextInput";
import Button from "../../../../common/components/button/Button";
import { useDispatch, useSelector } from "react-redux";
import SlideCommentCard from "./comment/SlideCommentCard";
import { AppState } from "../../../../root/RootReducer";
import PuiTypography from "../../../../components/elements/PuiTypography";
import { SlideComment } from "../../../../models/SlideComment";
import { AddSlideComment } from "../../../../store/actions/SlideActions";
import PuiButton from "../../../../components/elements/PuiButton";
import { FilledInput, FormControl, makeStyles, createStyles } from "@material-ui/core";
import { t } from "i18next";

interface SlideCommentPanelProps {
    documentId: number;
    pageNumber: number;
    setPageNumber: (pageNumber: number) => void;
    setIsUnderlKeyBorardControl: Dispatch<SetStateAction<boolean>>;
}

const useStyles = makeStyles(() =>
    createStyles({
        searchTitle: {
            color: "var(--primary-font)!important",
            transform: "none!important",
            height: "10px",
        },
        searchInput: {
            color: "var(--loading-bg)",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            width: "100%",
            paddingBottom: "16px",
            background: "none!important",
            "&:before": {
                borderBottom: "0.5px solid #333A43 !important",
            },
            "&:after": {
                borderBottom: "1px solid var(--studio-btn-bg)!important",
            },
            "& input": {
                padding: "0!important",
            },
        },
    })
);

function SlideCommentPanel({
    documentId,
    pageNumber,
    setPageNumber,
    setIsUnderlKeyBorardControl,
}: SlideCommentPanelProps) {
    const dispatch = useDispatch();
    const classes = useStyles();

    const [input, updateInput, resetInput] = useFormTextInput();

    const { userInfo, comments } = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo,
        comments: state.slide.slide?.comments,
    }));

    const [currentComments, setCurrentComments] = useState<SlideComment[]>(comments || []);
    const [isComment, setIsComment] = useState<boolean>(false);

    const cancelComment = () => {
        setIsComment(false);
        resetInput();
    };

    const submitComment = useCallback(() => {
        dispatch(
            AddSlideComment({
                documentId: documentId,
                userId: userInfo ? parseInt(userInfo.id) : -1,
                text: input.trim(),
                commentPages: pageNumber,
                isPrivate: false,
            })
        );
        resetInput();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId, userInfo, input, resetInput, pageNumber]);

    const isValidInput = useMemo(() => Boolean(input.trim()), [input]);

    const handleEnterKey = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter" && isValidInput) {
                submitComment();
            }
        },
        [submitComment, isValidInput]
    );

    const SetIsComment = () => {
        setIsComment(true);
        setIsUnderlKeyBorardControl(false);
    };

    const onFocusFunc = () => {
        setIsUnderlKeyBorardControl(false);
    };

    const onBlurFunc = () => {
        setIsUnderlKeyBorardControl(true);
    };

    useEffect(() => {
        if (!comments) return;
        const sortComment = comments.sort(function (a, b) {
            if (a.id < b.id) {
                return 1;
            } else {
                return -1;
            }
        });
        setCurrentComments(sortComment);
    }, [comments]);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div style={{ marginRight: "5px" }}>
                    <PuiTypography color="gray-80" size={20}>
                        {String(currentComments.length)}
                    </PuiTypography>
                </div>
                <PuiTypography color="gray-80" size={20} bold={true}>
                    {t("件のコメント")}
                </PuiTypography>
            </div>

            <div className={styles.input}>
                <div className={styles.icon}>
                    <img
                        src={userInfo?.imageUrl}
                        onError={(e) =>
                            (e.currentTarget.src =
                                require("../../../../assets/image/noPresenterImage.svg").default)
                        }
                        alt=""
                    />
                </div>

                <div className={styles.inputArea}>
                    <FormControl fullWidth variant="filled" color="secondary">
                        <FilledInput
                            id="searchInput"
                            onChange={updateInput}
                            onKeyPress={handleEnterKey}
                            onClick={SetIsComment}
                            onFocus={onFocusFunc}
                            onBlur={onBlurFunc}
                            placeholder={String(t("コメントを追加..."))}
                            autoComplete={"off"}
                            value={input}
                            className={classes.searchInput}
                        />
                    </FormControl>

                    {isComment && (
                        <div className={styles.inputRight}>
                            <Button
                                onClick={cancelComment}
                                color="white"
                                style={{
                                    border: "none",
                                    bottom: "16px",
                                    marginRight: "15px",
                                    color: "var(--primary-font)",
                                }}
                            >
                                {String(t("キャンセル"))}
                            </Button>
                            <div className={styles.submitComment}>
                                <PuiButton
                                    isActive={isValidInput}
                                    variant="contained"
                                    onClick={submitComment}
                                >
                                    {t("コメント")}
                                </PuiButton>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.comments}>
                {currentComments &&
                    currentComments.map((comment) => (
                        <SlideCommentCard
                            key={comment.id}
                            comment={comment}
                            setPageNumber={setPageNumber}
                            setIsUnderlKeyBorardControl={setIsUnderlKeyBorardControl}
                        />
                    ))}
            </div>
        </div>
    );
}

export default memo(SlideCommentPanel);
